import { useEffect, useRef } from "react";

import { useSite } from "~/contexts";
import { safeSessionStorage } from "~/lib/utils/safe-storage";
import { useRootLoaderData } from "~/root";

import { fireTpaConsent } from "./consent";

// Shares composable's session key name (`segment_consent_tracked`) so a shopper
// who bounces between an SFRA-rendered and a Workers-rendered page in the same
// session isn't double-asserted.
const SESSION_KEY = "segment_consent_tracked";

/**
 * TpaConsentTracker — programmatic `tpa-initial-load` marketing consent
 * (SFCC-11945). Ports hec-composable `TpaConsentTracker.js`, which the Workers
 * app never carried over; the emission disappeared when traffic cut to Workers,
 * so no `tpa-initial-load` consent has reached Bloomreach on the new app.
 *
 * A TPA-affiliate shopper who is marketing-allowed granted consent at the
 * affiliate before SSO'ing in — so on initial load we assert it on their behalf
 * once per session. Fire condition (all server-authoritative except the
 * fire-once latch):
 *  - `isTpaAffiliate` — an AFID is present (URL / `tpaBranding` cookie); the
 *    composable `isTpaUser` signal, resolved in the root loader.
 *  - `!segmentTpaSuppressed` — the shopper is marketing-allowed (explicit
 *    `c_tpaLearningCenterConsentData` OR the per-affiliate config flag). When a
 *    shopper opts in via the TPA welcome modal, `useSetTpaConsent` revalidates
 *    the root loader, this flag flips false, and the effect re-runs and fires —
 *    that revalidation is what makes the welcome-modal opt-in work without extra
 *    wiring here.
 *  - `customer.email` — hydrated, so `identify` has a userId before the consent
 *    (SFCC-10693). SFCC-11280: latch the session flag only AFTER email resolves,
 *    else the consent early-returns while the session is marked tracked and the
 *    consent is permanently suppressed.
 *
 * Mount once at the app root (alongside `<SegmentIdentify />`).
 */
export const TpaConsentTracker = () => {
  const { customer, isTpaAffiliate, segmentTpaSuppressed } =
    useRootLoaderData();
  const { site } = useSite();
  const email = customer?.email;

  // Fire-once-per-session latch. Shares composable's `segment_consent_tracked`
  // key, so a shopper who bounces between an SFRA- and Workers-rendered page in
  // one session isn't double-asserted. Trade-off (inherited from composable): a
  // logout + different-user re-login in the SAME tab won't re-fire.
  // SFCC-11876 — safeSessionStorage, NOT raw `sessionStorage`: `typeof window`
  // does not guard the GETTER, which throws SecurityError under Chrome "block
  // all cookies"/partitioned embeds; render-time here = offline boundary on
  // every page. Mirrors segment/identify.tsx.
  const hasTracked = useRef<boolean>(
    safeSessionStorage.getItem(SESSION_KEY) === "true",
  );
  // Did we ever observe this affiliate as NOT-yet-marketing-allowed before
  // firing? If so, the eventual fire is a mid-session opt-in (welcome-modal
  // checkbox → profile write → root revalidate) → placement `tpa-consent-change`
  // rather than `tpa-initial-load`. Mirrors composable's two placements.
  const sawSuppressed = useRef(false);

  useEffect(() => {
    if (hasTracked.current) return;
    if (!isTpaAffiliate) return;
    if (segmentTpaSuppressed) {
      // TPA affiliate not yet marketing-allowed — a later opt-in flips this.
      sawSuppressed.current = true;
      return;
    }
    // Wait for the SSO customer's email to hydrate before latching (SFCC-11280).
    if (!email) return;

    // Latch before firing so a concurrent effect re-run (revalidation /
    // navigation) can't double-assert while identify is pending.
    hasTracked.current = true;
    safeSessionStorage.setItem(SESSION_KEY, "true");
    void fireTpaConsent({
      email,
      siteId: site.id,
      placement: sawSuppressed.current
        ? "tpa-consent-change"
        : "tpa-initial-load",
    });
  }, [isTpaAffiliate, segmentTpaSuppressed, email, site.id]);

  return null;
};
